import { Box } from '@mui/material';
import { FC } from 'react';
import { UnassignedItem } from 'src/entities/dispatch';
import { formatDisplayDayLabel } from 'src/pages/Map/displayDays';

/**
 * 積日が引けない案件を集めるキー。'YYYY-MM-DD' とは形が違うので日付キーと衝突しない。
 */
export const NO_LOADING_DATE_KEY = '__no_loading_date__';

/** 積日の絞り込みを掛けないことを表す値。日付キー・NO_LOADING_DATE_KEY と衝突しない */
export const ALL_LOADING_DATES = 'all';

export type LoadingDateGroup = {
  /** 'YYYY-MM-DD' または NO_LOADING_DATE_KEY */
  key: string;
  label: string;
  items: UnassignedItem[];
};

export type LoadingDateOption = {
  /** 'YYYY-MM-DD' または NO_LOADING_DATE_KEY */
  key: string;
  label: string;
};

/**
 * 案件の積日キー。見出しと絞り込みで同じキーを引くために関数にしてある
 * （片方だけ `item.loadingDate` を直に読むと、空文字の扱いが食い違う）。
 */
export function loadingDateKeyOf(item: UnassignedItem): string {
  return item.loadingDate || NO_LOADING_DATE_KEY;
}

/**
 * 見出しのラベル。中央ボードの日付列と同じ関数で作る（見出しの日と列の日を目で突き合わせる）。
 */
export function loadingDateLabel(key: string): string {
  return key === NO_LOADING_DATE_KEY ? '日付未設定' : formatDisplayDayLabel(key);
}

/** 積日キーの並び。昇順で、日付未設定は末尾 */
function compareLoadingDateKeys(a: string, b: string): number {
  // 日付未設定は末尾。先頭に来ると、日付の並びを追う目が毎回そこで止まる
  if (a === NO_LOADING_DATE_KEY) return 1;
  if (b === NO_LOADING_DATE_KEY) return -1;
  return a.localeCompare(b);
}

/**
 * 積日セレクトの選択肢。存在する積日だけを見出しと同じキー・ラベル・並びで返す。
 *
 * Why not 画面側で組む: 見出しが `3/21(金)` でセレクトが `3/21` のように食い違うと、
 * どの見出しを選んだのか突き合わせられなくなる。
 */
export function buildLoadingDateOptions(items: readonly UnassignedItem[]): LoadingDateOption[] {
  const keys = new Set<string>();
  for (const item of items) keys.add(loadingDateKeyOf(item));

  return Array.from(keys)
    .sort(compareLoadingDateKeys)
    .map((key) => ({ key, label: loadingDateLabel(key) }));
}

/**
 * 積日ごとに束ねる。グループ内の並びは渡された順（= 検索・フィルタ・ソートの結果）のまま。
 *
 * Why not 各グループを並べ直す: 一覧の並び替えは画面上部のソートで操作するもので、
 * 束ねたせいでその結果が変わると、ソートを押しても効かない列があるように見える。
 */
export function groupItemsByLoadingDate(items: readonly UnassignedItem[]): LoadingDateGroup[] {
  const groups = new Map<string, UnassignedItem[]>();
  for (const item of items) {
    const key = loadingDateKeyOf(item);
    const bucket = groups.get(key);
    if (bucket) bucket.push(item);
    else groups.set(key, [item]);
  }

  return Array.from(groups.entries())
    .sort(([a], [b]) => compareLoadingDateKeys(a, b))
    .map(([key, groupItems]) => ({ key, label: loadingDateLabel(key), items: groupItems }));
}

type HeaderProps = {
  label: string;
  /** 配下の**伝票**件数。地点単位でもレーン数ではなく伝票で数える */
  count: number;
};

/** 積日グループの見出し。スクロールしても列の日付を見失わないよう上端に貼り付ける */
export const LoadingDateGroupHeader: FC<HeaderProps> = ({ label, count }) => (
  <Box
    data-testid="loading-date-group-header"
    sx={{
      position: 'sticky',
      // 一覧の上パディング (pt: 1 = 8px) ぶん引き上げて、カードの直前で止める
      top: -8,
      zIndex: 5,
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      // 左右のパディング (px: 1.5) を打ち消して端まで敷く
      mx: -1.5,
      px: 1.5,
      py: 0.5,
      bgcolor: 'hsl(var(--secondary) / 0.7)',
      backdropFilter: 'blur(4px)',
      borderBottom: '1px solid hsl(var(--border) / 0.5)',
      fontSize: 11,
      fontWeight: 700,
      color: 'hsl(var(--foreground))',
    }}
  >
    <Box component="span">{label}</Box>
    <Box component="span" sx={{ fontWeight: 500, color: 'hsl(var(--muted-foreground))' }}>
      {`${count}件`}
    </Box>
  </Box>
);
